import type { FC } from 'react';
import { useMemo } from 'react';

import { useGameConfig } from '../../../config/GameConfigProvider';
import { useChipSlice } from '../../../store/slices/chips/selectors';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { useSettingsSlice } from '../../../store/slices/settings/selectors';

import { ChipProps, Chip } from './Chip';

/**
 * # ChipConnected
 *
 * This component is a wrapper around `Chip` component. It calculates chip weight based on value using info from `tableConfig`. As well as chip color is taken from `tableConfig`, if such color config is provided.
 */
export const ChipConnected: FC<ChipProps> = ({ weight, value, ...rest }) => {
  const chips = useChipSlice.getChips();
  const locale = useSettingsSlice.currentLanguage();

  const calculatedWeight = useMemo(() => {
    if (weight) {
      return weight;
    }

    let calculatedWeight = 1;
    chips.forEach((chip) => {
      if (chip.value <= value && chip.weight > calculatedWeight) {
        calculatedWeight = chip.weight;
      }
    });

    return calculatedWeight;
  }, [chips, value, weight]);

  const chipColor = useChipColorConnected(calculatedWeight);

  return <Chip locale={locale} color={chipColor} weight={calculatedWeight} value={value} {...rest} />;
};

// fixme: Chip colors config should be a prop to test ChipStack independently in storybook: it is not a pure function due that.
const useChipColorConnected = (calculatedWeight: number): string => {
  const gameConfig = useGameConfig()?.config;
  const tableConfig = useConfigSlice.tableConfig();

  const chipColorsConfig = useMemo(() => {
    const chipColorsGameConfig = gameConfig?.overridingChipColorCodes || [];
    const chipColorsTableConfig = tableConfig?.chipColorCodes || [];

    return {
      chipColorsGameConfig,
      chipColorsTableConfig,
    };
  }, [gameConfig?.overridingChipColorCodes, tableConfig?.chipColorCodes]);

  // Prioritise chip colors from local game config over table config from backend
  return (
    chipColorsConfig.chipColorsGameConfig[calculatedWeight - 1] ||
    chipColorsConfig.chipColorsTableConfig[calculatedWeight - 1]
  );
};
